import React, { useEffect, useState } from "react"
import { HolidayDate, NagerAPIHoliday } from "./type"
import { SelectOption } from "../../types/SelectOption"
import { HolidaysTable } from "./HolidaysTable"
import Select from "react-select"

interface Props
{
    codePays?: string
    dealerships?: SelectOption[]
}

declare const window: any

export function ModalHolidaysDate(props: Props) {
    const currentYear = new Date().getFullYear()
    const NAGER_API_URL = "https://date.nager.at/api/v3/publicholidays"

    const [ selectedYear, setSelectedYear ] = useState<number>(currentYear)
    const [ holidaysDate, setHolidaysDate ] = useState<HolidayDate[]>([])
    const [ newHolidayName, setNewHolidayName ] = useState<string>("")
    const [ newHolidayDate, setNewHolidayDate ] = useState<string>("")
    const [ isLoading, setIsLoading ] = useState<boolean>(false)

    /**
     * Réinitialise le formulaire d'ajout manuel
     */
    const resetForm = (): void => {
        setNewHolidayName("")
        setNewHolidayDate("")
    }

    const yearOptions: SelectOption[] = [
        { label: String(currentYear - 1), value: currentYear - 1 },
        { label: String(currentYear), value: currentYear },
        { label: String(currentYear + 1), value: currentYear + 1 },
    ]

    /**
     * Récupère les jours fériés depuis le backend
     */
    const fetchHolidaysFromBackend = async (year: number): Promise<boolean> => {
        try {
            const response = await fetch(
                `${localStorage.backoffice_api}/api/holidaysDates/${year}`,
                {
                    method: "GET",
                    headers: {
                        authorization: localStorage.JWT,
                    }
                }
            )

            if (response.ok) {
                const json = await response.json()
                setHolidaysDate(json)
                return json.length > 0
            }
            return false
        } catch (e) {
            console.error("Error fetching holidays from backend", e)
            return false
        }
    }

    /**
     * Appelle l'API Nager et retourne les jours fériés formatés
     */
    const callNagerAPI = async (year: number): Promise<HolidayDate[]> => {
        const response = await fetch(
            `${NAGER_API_URL}/${year}/${props.codePays ?? "FR"}`
        )
        const json = await response.json() as NagerAPIHoliday[]

        return json.map((holiday) => ({
            name: holiday.localName,
            country: props.codePays ?? "FR",
            date: holiday.date,
            isManual: false,
            isRecurrent: true,
            dealershipIds: [],
        })).sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
    }

    /**
     * Récupère les jours fériés depuis l'API Nager et les définit comme données
     */
    const fetchHolidaysFromNagerAPI = async (year: number): Promise<void> => {
        try {
            setIsLoading(true)
            const formattedHolidays = await callNagerAPI(year)
            setHolidaysDate(formattedHolidays)
        } catch (e) {
            console.error("Error on fetchHolidays from Nager API", e)
        } finally {
            setIsLoading(false)
        }
    }

    /**
     * Fusionne intelligemment les jours fériés existants avec ceux de l'API
     * - Préserve les entrées manuelles
     * - Met à jour le nom des entrées API existantes
     * - Ajoute uniquement les nouvelles entrées API qui n'existent pas déjà
     */
    const mergeHolidays = (
        existing: HolidayDate[],
        fromAPI: HolidayDate[]
    ): HolidayDate[] => {
        const normalizeDate = (dateStr: string): string => {
            const date = new Date(dateStr)
            return date.toISOString().split('T')[0]
        }

        const existingMap = new Map<string, HolidayDate>()
        existing.forEach(holiday => {
            existingMap.set(normalizeDate(holiday.date), holiday)
        })

        fromAPI.forEach(apiHoliday => {
            const normalizedDate = normalizeDate(apiHoliday.date)
            const existingHoliday = existingMap.get(normalizedDate)

            if (existingHoliday) {
                if (existingHoliday.isManual) {
                    return
                }

                existingMap.set(normalizedDate, {
                    ...existingHoliday,
                    name: apiHoliday.name
                })
            } else {
                existingMap.set(normalizedDate, apiHoliday)
            }
        })

        return Array.from(existingMap.values()).sort((a, b) =>
            new Date(a.date).getTime() - new Date(b.date).getTime()
        )
    }

    /**
     * Recharge depuis l'API Nager et fusionne avec les données existantes
     */
    const reloadFromNagerAPI = async (): Promise<void> => {
        try {
            setIsLoading(true)
            const apiHolidays = await callNagerAPI(selectedYear)
            const merged = mergeHolidays(holidaysDate, apiHolidays)
            setHolidaysDate(merged)
        } catch (e) {
            console.error("Error reloading from Nager API", e)
        } finally {
            setIsLoading(false)
        }
    }

    /**
     * Sauvegarde les jours fériés
     * - Envoie tous les jours présents dans la liste
     * - dealershipIds = tableau vide [] si configuration globale
     * - dealershipIds = [id1, id2, ...] si sites spécifiques
     */
    const handleSave = async (): Promise<void> => {
        try {
            await fetch(`${localStorage.backoffice_api}/api/holidaysDates`, {
                method: 'POST',
                headers: {
                    "Content-Type": "application/json",
                    authorization: localStorage.JWT,
                },
                body: JSON.stringify({
                    holidays: holidaysDate,
                    year: selectedYear
                }),
            })

            window.$('#modalHolidaysDates').modal('hide')
        } catch (e) {
            console.error("ERREUR lors de la sauvegarde des jours fériés", e)
        }
    }

    /**
     * Ajoute un nouveau jour férié manuel
     */
    const handleAddNewHoliday = (): void => {
        if (!newHolidayName || !newHolidayDate) {
            return
        }

        const newHoliday: HolidayDate = {
            name: newHolidayName,
            country: props.codePays,
            date: newHolidayDate,
            isManual: true,
            isRecurrent: false,
            dealershipIds: [],
        }

        setHolidaysDate(prev => {
            const updated = [ ...prev, newHoliday ]
            return updated.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
        })

        setNewHolidayName("")
        setNewHolidayDate("")
    }

    /**
     * Supprime un jour férié
     */
    const handleDeleteHoliday = (index: number): void => {
        setHolidaysDate(prev => prev.filter((h, i) => i !== index))
    }

    /**
     * Change les sites d'un jour férié
     */
    const handleDealershipChange = (index: number, dealershipIds: number[]): void => {
        setHolidaysDate(prev =>
            prev.map((h, i) =>
                i === index ? { ...h, dealershipIds } : h
            )
        )
    }

    /**
     * Charge les jours fériés au changement d'année
     * - Tente d'abord de charger depuis le backend
     * - Si aucun jour férié trouvé, charge automatiquement depuis l'API Nager
     */
    useEffect(() => {
        const loadHolidays = async () => {
            const hasBackendData = await fetchHolidaysFromBackend(selectedYear)
            if (!hasBackendData) {
                await fetchHolidaysFromNagerAPI(selectedYear)
            }
        }
        loadHolidays()
    }, [selectedYear])

    return (
        <div id="modalHolidaysDates" className="modal fade" tabIndex={-1} role="dialog" data-backdrop="static" data-keyboard="false">
            <div className="modal-dialog modal-dialog-centered modal-xl">
                <div className="modal-content border-0">
                    <div className="modal-header p-2 bg-light">
                        <span className="tw-text-xl tw-font-semibold">Jours fériés</span>
                    </div>

                    <div className="modal-body">
                        <div className="tw-flex tw-flex-col tw-gap-4">
                            <div className="tw-flex tw-flex-row tw-justify-between tw-items-center">
                                <div className="tw-flex tw-flex-row tw-items-center tw-gap-3">
                                    <span className="tw-font-medium">Année :</span>
                                    <Select
                                        style={{ minWidth: '8em' }}
                                        value={yearOptions.find(opt => opt.value === selectedYear)}
                                        onChange={(option: SelectOption | null) => {
                                            if (option) setSelectedYear(option.value as number)
                                        }}
                                        options={yearOptions}
                                    />
                                </div>
                                <button
                                    type="button"
                                    className="btn btn-secondary tw-text-sm"
                                    onClick={reloadFromNagerAPI}
                                    disabled={isLoading}
                                >
                                    <i className="fa fa-refresh tw-mr-2"></i>
                                    Recharger les jours fériés
                                </button>
                            </div>

                            {isLoading ? (
                                <div className="tw-text-center tw-py-4">
                                    <i className="fa fa-spinner fa-spin tw-mr-2"></i>
                                    Chargement...
                                </div>
                            ) : (
                                <HolidaysTable
                                    holidays={holidaysDate}
                                    dealerships={props.dealerships || []}
                                    onDealershipChange={handleDealershipChange}
                                    onDelete={handleDeleteHoliday}
                                />
                            )}

                            <div className="tw-border-t tw-pt-4">
                                <h6 className="tw-font-semibold tw-mb-3">Ajouter un jour férié manuel</h6>
                                <div className="tw-flex tw-flex-row tw-items-center tw-gap-3">
                                    <input
                                        type="text"
                                        className="tw-border tw-border-gray-300 tw-rounded tw-px-3 tw-py-2 tw-w-1/3"
                                        placeholder="Nom du jour férié"
                                        value={newHolidayName}
                                        onChange={(e) => setNewHolidayName(e.target.value)}
                                    />
                                    <input
                                        type="date"
                                        className="tw-border tw-border-gray-300 tw-rounded tw-px-3 tw-py-2"
                                        value={newHolidayDate}
                                        onChange={(e) => setNewHolidayDate(e.target.value)}
                                    />
                                    <button
                                        type="button"
                                        className="btn btn-primary tw-text-sm"
                                        onClick={handleAddNewHoliday}
                                        disabled={!newHolidayName || !newHolidayDate}
                                    >
                                        <i className="fa fa-plus tw-mr-2"></i>
                                        Ajouter
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="modal-footer">
                        <button type="button" className="btn btn-secondary" data-dismiss="modal" onClick={resetForm}>Fermer</button>
                        <button
                            type="button"
                            onClick={handleSave}
                            className="btn btn-primary"
                        >
                            Enregistrer
                        </button>
                    </div>
                </div>
            </div>
        </div>
    )
}
